// Dok 17 – Arbetsmiljökrav i upphandlingsskedet.
// Samlar de krav som ska föras in i förfrågningsunderlag och kontrakt, med källa,
// ansvarig och status, så att inget faller mellan projektering och utförande.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentHeader from "./DocumentHeader";
import { FileSignature, Plus, Trash2, Sparkles } from "lucide-react";

interface Props {
  projectId: string;
}

interface Row {
  id: string;
  title: string;
  category: string;
  requirement: string | null;
  source_doc: string | null;
  responsible: string | null;
  included: boolean;
  status: string;
  note: string | null;
  sort_order: number;
}

const CATEGORIES: { value: string; label: string }[] = [
  { value: "krav", label: "Krav i förfrågan" },
  { value: "kompetens", label: "Kompetens och behörighet" },
  { value: "skydd", label: "Gemensamma skyddsanordningar" },
  { value: "etablering", label: "Etablering och APD" },
  { value: "kontrakt", label: "Kontraktsvillkor" },
];

const STATUS: { value: string; label: string }[] = [
  { value: "ej_klar", label: "Ej klar" },
  { value: "pagaende", label: "Pågående" },
  { value: "klar", label: "Klar" },
  { value: "ej_aktuell", label: "Ej aktuell" },
];

/** Standardkrav som täcker det som normalt måste finnas i en förfrågan. */
const TEMPLATE: Omit<Row, "id">[] = [
  {
    title: "Arbetsmiljöplanen bifogas förfrågningsunderlaget",
    category: "krav",
    requirement:
      "Aktuell arbetsmiljöplan med särskilda risker bifogas så att anbudsgivarna kan prissätta skyddsåtgärderna.",
    source_doc: "Dok 12",
    responsible: "Bas-P",
    included: false,
    status: "ej_klar",
    note: null,
    sort_order: 1,
  },
  {
    title: "Kvarvarande risker från projektörerna redovisas",
    category: "krav",
    requirement: "Risker som inte gick att projektera bort redovisas i förfrågan.",
    source_doc: "Dok 16, Dok 11",
    responsible: "Bas-P",
    included: false,
    status: "ej_klar",
    note: null,
    sort_order: 2,
  },
  {
    title: "Krav på kompetens och behörigheter",
    category: "kompetens",
    requirement:
      "Entreprenören ska kunna visa behörigheter för heta arbeten, ställning, lyft, asbest och el.",
    source_doc: "Dok 24",
    responsible: "Byggherre",
    included: false,
    status: "ej_klar",
    note: null,
    sort_order: 3,
  },
  {
    title: "Farligt material och saneringsbehov redovisas",
    category: "krav",
    requirement: "Inventering av asbest, PCB och annat farligt material bifogas förfrågan.",
    source_doc: "Dok 08",
    responsible: "Bas-P",
    included: false,
    status: "ej_klar",
    note: null,
    sort_order: 4,
  },
  {
    title: "Ansvar för gemensamma skyddsanordningar regleras",
    category: "skydd",
    requirement:
      "Vem som sätter upp, underhåller och tar ned räcken, ställningar och belysning skrivs in i kontraktet.",
    source_doc: "Dok 26",
    responsible: "Bas-U",
    included: false,
    status: "ej_klar",
    note: null,
    sort_order: 5,
  },
  {
    title: "Etablering, APD-plan och tredje man",
    category: "etablering",
    requirement:
      "Krav på etableringsytor, transportvägar och skydd av tredje man förs in i förfrågan.",
    source_doc: "Dok 07, Dok 26",
    responsible: "Bas-P",
    included: false,
    status: "ej_klar",
    note: null,
    sort_order: 6,
  },
  {
    title: "Krav på arbetsberedning innan riskfyllda moment",
    category: "kontrakt",
    requirement:
      "Entreprenören ska lämna arbetsberedning som godkänns av Bas-U innan momentet startar.",
    source_doc: "Dok 24",
    responsible: "Bas-U",
    included: false,
    status: "ej_klar",
    note: null,
    sort_order: 7,
  },
  {
    title: "Krav på rapportering av tillbud och olyckor",
    category: "kontrakt",
    requirement: "Tillbud, olyckor och allvarliga händelser ska rapporteras utan dröjsmål.",
    source_doc: "Dok 28",
    responsible: "Bas-U",
    included: false,
    status: "ej_klar",
    note: null,
    sort_order: 8,
  },
  {
    title: "Tid för arbetsmiljöåtgärder finns i tidplanen",
    category: "kontrakt",
    requirement: "Tidplanen ger utrymme för skyddsarbete, samordning och avetablering.",
    source_doc: "Dok 06",
    responsible: "Byggherre",
    included: false,
    status: "ej_klar",
    note: null,
    sort_order: 9,
  },
];

export const ProcurementRequirements: React.FC<Props> = ({ projectId }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("procurement_requirements")
      .select("id, title, category, requirement, source_doc, responsible, included, status, note, sort_order")
      .eq("project_id", projectId)
      .order("sort_order");
    if (error) toast.error("Kunde inte läsa upphandlingskraven");
    setRows((data as Row[] | null) ?? []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const seed = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("procurement_requirements").insert(
      TEMPLATE.map((t) => ({ ...t, project_id: projectId, created_by: auth.user?.id ?? null }))
    );
    if (error) toast.error("Kunde inte skapa standardkraven");
    else {
      toast.success("Standardkraven är tillagda");
      void load();
    }
  };

  const add = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("procurement_requirements").insert({
      project_id: projectId,
      title: "Nytt krav",
      category: "krav",
      sort_order: rows.length + 1,
      created_by: auth.user?.id ?? null,
    });
    if (error) toast.error("Kunde inte lägga till kravet");
    else void load();
  };

  const save = async (id: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const { error } = await supabase.from("procurement_requirements").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("procurement_requirements").delete().eq("id", id);
    if (error) toast.error("Kunde inte ta bort");
    else setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const relevant = rows.filter((r) => r.status !== "ej_aktuell");
  const done = relevant.filter((r) => r.status === "klar").length;
  const percent = relevant.length ? Math.round((done / relevant.length) * 100) : 0;

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="17"
        title="Arbetsmiljökrav i upphandling"
        description="Kraven som ska in i förfrågningsunderlag och kontrakt innan entreprenören handlas upp. Underlaget hämtas från riskerna, tidplanen och arbetsmiljöplanen."
        icon={FileSignature}
        actions={
          <div className="flex flex-wrap gap-2">
            {rows.length === 0 && (
              <Button onClick={seed}>
                <Sparkles className="mr-2 h-4 w-4" /> Hämta standardkrav
              </Button>
            )}
            <Button variant="outline" onClick={add}>
              <Plus className="mr-2 h-4 w-4" /> Lägg till krav
            </Button>
          </div>
        }
      />

      <Card>
        <CardContent className="space-y-2 pt-6">
          <Progress value={percent} />
          <p className="text-sm text-muted-foreground">
            {done} av {relevant.length} krav är klara ({percent} %).
          </p>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Inga krav ännu. Hämta standardkraven så får du en färdig lista att stryka i och komplettera.
          </CardContent>
        </Card>
      ) : (
        rows.map((r) => (
          <Card key={r.id}>
            <CardContent className="space-y-3 pt-6">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <Input
                  className="max-w-xl font-medium"
                  defaultValue={r.title}
                  onBlur={(e) => save(r.id, { title: e.target.value })}
                />
                <div className="flex items-center gap-2">
                  <Badge variant={r.status === "klar" ? "default" : "outline"}>
                    {STATUS.find((s) => s.value === r.status)?.label ?? r.status}
                  </Badge>
                  <Button variant="ghost" size="icon" onClick={() => remove(r.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-4">
                <div className="space-y-1">
                  <Label>Kategori</Label>
                  <Select value={r.category} onValueChange={(v) => save(r.id, { category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select value={r.status} onValueChange={(v) => save(r.id, { status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Underlag</Label>
                  <Input
                    defaultValue={r.source_doc ?? ""}
                    placeholder="Dok 12"
                    onBlur={(e) => save(r.id, { source_doc: e.target.value || null })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Ansvarig</Label>
                  <Input
                    defaultValue={r.responsible ?? ""}
                    placeholder="Bas-P"
                    onBlur={(e) => save(r.id, { responsible: e.target.value || null })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label>Så här formuleras kravet</Label>
                <Textarea
                  rows={2}
                  defaultValue={r.requirement ?? ""}
                  onBlur={(e) => save(r.id, { requirement: e.target.value || null })}
                />
              </div>

              <div className="space-y-1">
                <Label>Anteckning</Label>
                <Input
                  defaultValue={r.note ?? ""}
                  placeholder="Var i förfrågan kravet ligger, avsteg m.m."
                  onBlur={(e) => save(r.id, { note: e.target.value || null })}
                />
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default ProcurementRequirements;
